import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/auth.context';
import Api from '../axiosConfig';
import '../style/userprofile.css';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navbar from './Navbar';

function UserProfile() {
  const [userPosts, setUserPosts] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [bio, setBio] = useState('');
  const [newProfilePicture, setNewProfilePicture] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBioModal, setShowBioModal] = useState(false);
  const [updatedBio, setUpdatedBio] = useState('');
  const { state } = useContext(AuthContext);
  const { userId } = useParams();

  useEffect(() => {
    const fetchUserPosts = async () => {
      setLoading(true);
      try {
        const response = await Api.post('/auth/user-posts', { userId });
        if (response.data.success) {
          setUserPosts(response.data.posts);
        } else {
          console.error('Failed to fetch posts');
        }
      } catch (error) {
        console.error('Error fetching user posts', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserData = async () => {
      try {
        const response = await Api.get(`/auth/user/${userId}`);
        if (response.data.success) {
          const { username, name, profilePicture, followersCount, followingCount, bio } = response.data;
          setUserData({
            username,
            name,
            profilePicture,
            followersCount,
            followingCount,
            bio,
          });
          setBio(bio); // Store fetched bio
          setUpdatedBio(bio); // Set initial value for updated bio
        } else {
          console.error('Failed to fetch user data:', response.data.error);
        }
      } catch (error) {
        console.error('Error fetching user data', error);
      }
    };

    if (state?.user?.userId) {
      fetchUserPosts();
      fetchUserData();
    }
  }, [state, userId]);

  const handleUpdateBio = async () => {
    try {
      const response = await Api.post('/auth/update-bio', { userId: state.user.userId, bio: updatedBio });
      if (response.data.success) {
        setBio(updatedBio); // Update the local bio state to reflect the change
        setUserData((prev) => ({ ...prev, bio: updatedBio })); // Update userData with new bio
        toast.success('Bio updated successfully!');
        setShowBioModal(false); // Close modal
      } else {
        toast.error('Failed to update bio.');
      }
    } catch (error) {
      toast.error('Error updating bio.');
    }
  };

  const handleUpdateProfilePicture = async () => {
    try {
      const response = await Api.post('/auth/update-profile-picture', { userId: state.user.userId, profilePicture: newProfilePicture });
      if (response.data.success) {
        setUserData((prev) => ({ ...prev, profilePicture: newProfilePicture }));
        toast.success('Profile picture updated successfully!');
      } else {
        toast.error('Failed to update profile picture.');
      }
    } catch (error) {
      toast.error('Error updating profile picture.');
    }
  };

  const handleFollow = async () => {
    try {
      const response = await Api.post('/auth/follow', { userId: state.user.userId, followUserId: userId });
      if (response.data.success) {
        setIsFollowing(true);
        setUserData(prevData => ({
          ...prevData,
          followersCount: prevData.followersCount + 1
        }));
        toast.success('Followed successfully!');
      } else {
        toast.error('Failed to follow user.');
      }
    } catch (error) {
      toast.error('Error following user.');
    }
  };

  const handleUnfollow = async () => {
    try {
      const response = await Api.post('/auth/unfollow', { userIdToUnfollow: userId, userId: state.user.userId });
      if (response.data.success) {
        setIsFollowing(false);
        setUserData(prevData => ({
          ...prevData,
          followersCount: prevData.followersCount - 1
        }));
        toast.success('Unfollowed successfully!');
      } else {
        toast.error('Failed to unfollow user.');
      }
    } catch (error) {
      toast.error('Error unfollowing user.');
    }
  };

  return (
    <div className='userprofile-parent'>
      <Navbar />
      <div className="userprofile">

        {userData ? (
          <div className="userprofiletopmain">
            <div className="userprofiletop">
              <img
                src={userData.profilePicture}
                alt={`${userData.username}'s profile`}
                className="profile-picture"
                onClick={() => setShowModal(true)}
              />
            </div>
            {/* Modal for updating profile picture */}
            {showModal && (
              <div className="modal">
                <div className="modal-content">
                  <span className="close" onClick={() => setShowModal(false)}>&times;</span>
                  <h2>Update Profile Picture</h2>
                  <input
                    type="text"
                    value={newProfilePicture}
                    onChange={(e) => setNewProfilePicture(e.target.value)}
                    placeholder="Enter new profile picture URL"
                  />
                  <button onClick={handleUpdateProfilePicture}>Update</button>
                </div>
              </div>
            )}
            {/* Modal for updating bio */}
            {showBioModal && (
              <div className="modal">
                <div className="modal-content">
                  <span className="close" onClick={() => setShowBioModal(false)}>&times;</span>
                  <h2>Update Bio</h2>
                  <textarea
                    value={updatedBio}
                    onChange={(e) => setUpdatedBio(e.target.value)}
                    placeholder="Enter your bio"
                  />
                  <button onClick={handleUpdateBio}>Update Bio</button>
                </div>
              </div>
            )}
            <div className="user-profile-namemain">
              <span className="user-profile-name">{userData.username}</span>
              <div className="user-followmaindiv">
                <span className="user-followers">Followers: {userData.followersCount || 0}</span>
                <span className="user-following">Following: {userData.followingCount || 0}</span>
                {isFollowing ? (
                  <button className="followButton following" onClick={handleUnfollow}>Unfollow</button>
                ) : (
                  <button className="followButton" onClick={handleFollow}>Follow</button>
                )}
              </div>
              <div className="bio-section">
                <p>{bio}</p> {/* Display current bio */}
                <button onClick={() => { setUpdatedBio(bio); setShowBioModal(true); }}>Update Bio</button>
              </div>
            </div>
          </div>
        ) : (
          <div>No user data available</div>
        )}
        {loading ? (
          <div>Loading... </div>
        ) : (
          <div className="userpostsmain">
            <div className="userpostsicon">
              <p><i className="fa-solid fa-border-all"></i>Posts</p>
              <p><i className="fa-solid fa-clapperboard"></i>Reels</p>
              <p><i className="fa-regular fa-bookmark"></i>Saved</p>
              <p><i className="fa-regular fa-id-badge"></i>Tagged</p>
            </div>

            <div className="userposts">
              {userPosts.map((post) => (
                <div key={post._id} className="post">
                  <img src={post.image} alt={post.caption} className="post-image" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;
